import { RankingBadge, StatusBadge, Text } from 'components';
import {
  TEXT_STRING,
  TIMEZONE,
  READY_TO_GO_TIME,
  TIME_FORMAT,
  DATE_FORMAT,
  OWN_COMPANY_FRANCHISE_ID,
} from '../../constants';
import { TDiscount, TFemale } from 'types';
import { useCallback, useMemo } from 'react';
import { formatAmountNumber, getBusinessDate } from 'utils';
import NoAvatar from '../../assets/images/avatar.svg';
import moment from 'moment-timezone';
import { useAppStore } from 'store';
import {
  resolveSearchCardAreaBadge,
  shouldUseAffiliationAreaForSearchBadge,
} from './resolveSearchCardAreaBadge';
import { Link } from 'react-router-dom';

type CardItemProps = {
  detailHref: string;
  onSelect?: () => void;
  data: TFemale;
  discount?: TDiscount;
  showShiftTime?: boolean;
  startDate?: string;
  rank?: number;
  /** True when search is filtered by activityAreas (backend Ready-to-Go uses p.areaId). */
  hasAreaFilter?: boolean;
};

const searchStr = TEXT_STRING.SEARCH_PAGE;

function CardItem({
  detailHref,
  onSelect,
  data,
  showShiftTime = false,
  discount,
  startDate,
  rank,
  hasAreaFilter = false,
}: CardItemProps) {
  const activityAreas = useAppStore((state) => state.activityAreas);

  const concatPrice = useCallback((price: number, text: string) => {
    return `${formatAmountNumber(price)}${text}`;
  }, []);

  // API contract: shifts[0] is the earliest shift of the selected day
  // (GROUP_CONCAT ... ORDER BY fs.startTime, fs.id).
  const shiftData = useMemo(() => data.shifts?.[0], [data.shifts]);
  const useAffiliationArea = useMemo(
    () =>
      data.displayArea?.name
        ? false
        : shouldUseAffiliationAreaForSearchBadge({
            isReadyToGo: data.isReadyToGo,
            selectedDate: startDate,
            shiftStartTime: shiftData?.startTime,
            hasAreaFilter,
          }),
    [
      data.displayArea?.name,
      data.isReadyToGo,
      hasAreaFilter,
      startDate,
      shiftData?.startTime,
    ],
  );
  const areaBadgeText = useMemo(() => {
    if (data.displayArea?.name) {
      return data.displayArea.name;
    }
    return resolveSearchCardAreaBadge({
      affiliationAreaName: data.area?.name,
      shiftAreaId: shiftData?.areaId,
      areas: activityAreas,
      useAffiliationArea,
    });
  }, [
    activityAreas,
    data.area?.name,
    data.displayArea?.name,
    shiftData?.areaId,
    useAffiliationArea,
  ]);
  const showReadyToGo = useMemo(() => {
    const currentTime = moment().tz(TIMEZONE);
    const selectedDate = moment(startDate).tz(TIMEZONE).format(DATE_FORMAT);

    if (
      !data.isReadyToGo ||
      selectedDate !== getBusinessDate(currentTime, DATE_FORMAT)
    )
      return false;
    if (!shiftData) return true;

    // Show "Go Now" if the current time is between 6:00 and 20:00
    const beginTime = moment(READY_TO_GO_TIME.BEGIN, TIME_FORMAT).tz(TIMEZONE);
    const endTime = moment(READY_TO_GO_TIME.END, TIME_FORMAT).tz(TIMEZONE);

    return currentTime.isBetween(beginTime, endTime, 'minute', '[]');
  }, [data.isReadyToGo, startDate, shiftData]);

  const ImageWithBadges = () => (
    <>
      <img
        src={data.avatar}
        alt={data.nickname}
        className="w-full aspect-[0.82] object-cover object-top rounded-t-lg"
        onError={(e) => (e.currentTarget.src = NoAvatar)}
      />
      <div className="absolute left-1 top-1">
        <StatusBadge
          icon="location"
          iconColor="#FF9500"
          text={areaBadgeText}
          bgOpacity={45}
        />
      </div>
      {(data.isMeeting || data.isPeriodTime) && (
        <div className="absolute right-1 top-1 flex flex-col gap-1">
          {data.isMeeting && (
            <StatusBadge
              icon="groupUser"
              iconColor="white"
              text={searchStr.MEETING}
              bgColor="linear-gradient(90deg, #FFD25A 0%, #FF8A5A 100%)"
            />
          )}
          {data.isPeriodTime && (
            <StatusBadge
              icon="bloodDrop"
              iconColor="white"
              text={searchStr.PERIOD_TIME}
              bgColor="linear-gradient(90deg, #D86DF5 0%, #FFB7D5 100%)"
            />
          )}
        </div>
      )}
      <RankingBadge rank={rank} />
    </>
  );

  const ItemInfoText = ({ className = '' }: { className?: string }) => {
    if (showShiftTime) {
      return (
        <Text className={`line-clamp-1 ${className}`}>
          {showReadyToGo
            ? searchStr.GO_NOW
            : shiftData
              ? `${shiftData.startTime} - ${shiftData.endTime}`
              : searchStr.TO_BE_CONFIRM}
        </Text>
      );
    }

    return (
      <Text className={`line-clamp-1 ${className}`}>
        {`${data.height || ''}cm(${data.cupSize}${TEXT_STRING.CAST_PAGE.CUP})`}
      </Text>
    );
  };

  const ItemInfo = ({ center = true }: { center?: boolean }) => {
    if (discount) {
      return (
        <>
          <div
            className={`flex items-center ${
              center && 'justify-center'
            } gap-2 w-full`}
          >
            <span className="font-bold text-[14px]">{data.nickname}</span>
            {data.franchiseId !== 1 && (
              <span className="font-medium text-[10px]">
                ({searchStr.FRANCHISE})
              </span>
            )}
          </div>
          <ItemInfoText className="text-[110%]" />
        </>
      );
    }

    return <ItemInfoText />;
  };

  if (discount) {
    return (
      <Link
        to={detailHref}
        onClick={onSelect}
        className="grid grid-cols-3 gap-5 text-neutral-900 no-underline focus-visible:rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
      >
        <div className="col-span-1 flex flex-col gap-2 justify-center items-center w-full">
          <div className="w-full relative">
            <ImageWithBadges />
          </div>
        </div>
        <div className="col-span-2 gap-2 flex flex-col justify-between">
          <div className="flex flex-col gap-2">
            <ItemInfo center={false} />
          </div>
          <div
            className="bg-primary-50 border border-solid border-primary-300 p-4 rounded-lg flex flex-col gap-2 justify-end"
          >
            <Text>
              {concatPrice(
                data.singleDiscount.amount,
                searchStr.DISCOUNT_MINIMUM,
              )}
            </Text>
            <Text fontSize={20} bold textColor="text-primary">
              {concatPrice(
                data.singleDiscount.applied,
                TEXT_STRING.COMMON.DISCOUNT,
              )}
            </Text>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <Link
        to={detailHref}
        onClick={onSelect}
        className="flex flex-col gap-2 justify-center items-center w-full text-neutral-900 no-underline focus-visible:rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
      >
        <div className="w-full relative">
          <ImageWithBadges />
          <div className="absolute bottom-0 left-0 right-0 bg-black/40 py-1 flex items-center justify-center gap-1">
            <span className="text-white font-bold text-[14px]">
              {data.nickname}
            </span>
            {data.franchiseId !== OWN_COMPANY_FRANCHISE_ID && (
              <span className="text-white font-medium text-[10px]">
                ({searchStr.FRANCHISE})
              </span>
            )}
          </div>
        </div>
        <ItemInfo />
      </Link>
    </div>
  );
}

export default CardItem;
